'use client';

import {
  Box,
  Flex,
  freddyTheme,
  Link,
  List,
  ListItem,
  SpriteIcon,
  Typography
} from '@coolblue-development/freddy';
import readExtendedExplanationTrigger from '@coolblue-development/ga4-events/triggers/InsuranceInformation/readExtendedExplanationTrigger';
import { Insurance as InsuranceIcon } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { IconWithText, SquareProductImage } from '@coolblue-development/next-patterns/client';
import type { InsuranceProduct as InsuranceProductType } from '@features/product/typings';
import useTestId from '@hooks/useTestId';
import { useTranslation } from '@i18n/useTranslation';

import InsuranceProduct from './InsuranceProduct';
import type { InsuranceInformationProps } from './types';
import { getInsurableProduct } from './utils/getInsurableProduct';

export default function InsuranceInformation({
  product,
  insurance,
  urls
}: InsuranceInformationProps) {
  const { t } = useTranslation('webshop.productPage.insurance');
  const { trackEvent } = useAnalytics();
  const testId = useTestId('insurance-block-nl');

  if (!insurance) {
    return null;
  }

  const insurableProduct = getInsurableProduct(product, insurance);

  const insuranceUsps =
    (insurance.summary.usps ?? []).length > 0
      ? (insurance.summary.usps ?? [])
      : [
          t('insuranceUsps.coolblueServiceFlow'),
          t('insuranceUsps.repairOptions'),
          t('insuranceUsps.reflectionPeriod')
        ];

  const insuranceProducts = insurance.packages.flatMap(
    insurancePackage => insurancePackage.products
  );

  if (insuranceProducts.length === 0) {
    return null;
  }

  return (
    <Flex gap={400} {...testId}>
      <Flex gap={400} direction='column' alignItems='flex-start'>
        <Typography variant='h2' mb={0}>
          {t('title', { productType: insurableProduct.productType.singularName })}
        </Typography>

        <List marker='none' gap='none'>
          {insuranceUsps.map((usp: string, index: number) => (
            <ListItem key={index}>
              <IconWithText
                description={
                  <Box ml={100}>
                    <Typography mb={0}>{usp}</Typography>
                  </Box>
                }
                icon={
                  <Box sx={{ svg: { marginBottom: freddyTheme.spacing[100] } }}>
                    <SpriteIcon name='checkmark' size='medium' color='sentimentPositive' />
                  </Box>
                }
                alignItems='flex-start'
              />
            </ListItem>
          ))}
        </List>

        <Box>
          <Typography variant='h3'>
            {insurance.summary.instructions.title ??
              t('packagesBlockTitle', { productType: insurableProduct.productType.singularName })}
          </Typography>

          <Typography mb={0}>{insurance.description}</Typography>
        </Box>

        <List marker='none' sx={{ width: '100%' }}>
          {insuranceProducts.map((insuranceProduct: InsuranceProductType, index: number) => (
            <InsuranceProduct
              insuranceProduct={insuranceProduct}
              key={index}
              primaryProductId={product.id}
              provider={insurance.partner}
            />
          ))}
        </List>

        <Link
          target='_blank'
          href={urls.insuranceMoreInformationUrl}
          startIcon={<SpriteIcon name='arrow-right' />}
          onClick={() =>
            trackEvent(
              readExtendedExplanationTrigger({
                sub_feature: 'product_page',
                index: 1,
                url: urls.insuranceMoreInformationUrl,
                redirect: t('readMoreAboutInsurances'),
                total_indices: 1
              })
            )
          }
        >
          {t('readMoreAboutInsurances')}
        </Link>
      </Flex>
      <Box hideUntil='md'>
        <SquareProductImage
          alt={insurableProduct.name}
          imageId={String(insurableProduct.primaryImage.id)}
          size='large'
          icon={<InsuranceIcon color={freddyTheme.color.icon.base} innerFill='contrast' />}
          iconPosition='right'
          fileCollection='product'
          loading='lazy'
        />
      </Box>
    </Flex>
  );
}
